// Small reusable field components for the Method Writers form.
// Expects EB Garamond + JetBrains Mono to be loaded globally.

import React, { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from "react";
import type { Genre } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Labelled field shell
interface FieldProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  error?: string;
  children?: ReactNode;
  note?: ReactNode;
}
function Field({ id, label, hint, required, error, children, note }: FieldProps) {
  return (
    <div className="field" data-error={!!error || undefined}>
      <label className="field-label" htmlFor={id}>
        <span className="field-label-text">{label}</span>
        {required && <span className="field-required" aria-label="required">*</span>}
      </label>
      {hint && <div className="field-hint">{hint}</div>}
      {children}
      <div className="field-foot">
        <div className="field-error">{error || "\u00A0"}</div>
        {note && <div className="field-note">{note}</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
interface TextInputProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  onBlur?: () => void;
  invalid?: boolean;
}
function TextInput({ id, value, onChange, placeholder, autoComplete, onBlur, invalid }: TextInputProps) {
  return (
    <input
      id={id}
      className="input"
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-invalid={invalid || undefined}
      spellCheck="false"
    />
  );
}

interface EmailInputProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
}
function EmailInput({ id, value, onChange, onBlur, invalid }: EmailInputProps) {
  return (
    <input
      id={id}
      className="input"
      type="email"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      data-invalid={invalid || undefined}
      autoComplete="email"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Segmented poetry/prose selector
function GenreSelect({ value, onChange }: { value: Genre | ""; onChange: (v: Genre) => void }) {
  const options: Array<{ value: Genre; label: string; sub: string }> = [
    { value: "poetry", label: "Poetry", sub: "Verse, stanzas, lines" },
    { value: "prose", label: "Prose", sub: "Paragraphs, ≤ 500 words" },
  ];
  return (
    <div className="genre-grid" role="radiogroup" aria-label="Submission type">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className="genre-card"
          data-selected={value === o.value || undefined}
          onClick={() => onChange(o.value)}
        >
          <div className="genre-card-mark" aria-hidden="true">
            <span />
          </div>
          <div className="genre-card-body">
            <div className="genre-card-title">{o.label}</div>
            <div className="genre-card-sub">{o.sub}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sanitize pasted/typed HTML: keep ONLY <em>/<i> + line breaks. Strip everything
// else — styles, colors, fonts, sizes, headings, lists, tables, images, bold,
// underline, etc. Returns { html, stripped: [list of kinds removed] }.
//
// preserveStructure=true skips the structural cleanups that paste needs but
// submit doesn't — specifically, the strips of leading top-level <br>s and
// <br>s sitting between <p> siblings. Those rules clean up paste artifacts
// from TextEdit/Word but would merge a writer's intentional Shift+Return
// line breaks when applied to live editor state on submit.
function sanitizeRichText(dirtyHtml: string, opts: { preserveStructure?: boolean } = {}): { html: string; stripped: string[] } {
  const kept = new Set(["EM", "I", "BR", "P", "DIV", "SPAN"]);
  // Kinds we consider "weird formatting" and will report back to the user.
  const flagged = {
    bold: /<\s*(b|strong)\b|font-weight\s*:\s*(bold|[6-9]00)/i,
    underline: /<\s*u\b|text-decoration\s*:\s*[^;"']*underline/i,
    color: /(^|[^-])color\s*:\s*[^;"']+/i,
    background: /background(-color)?\s*:\s*[^;"']+/i,
    fontFamily: /font-family\s*:/i,
    fontSize: /font-size\s*:/i,
    heading: /<\s*h[1-6]\b/i,
    list: /<\s*(ul|ol|li)\b/i,
    table: /<\s*(table|tr|td|th)\b/i,
    image: /<\s*img\b/i,
    link: /<\s*a\b/i,
    align: /text-align\s*:/i,
    strike: /<\s*(s|strike|del)\b|text-decoration\s*:\s*[^;"']*line-through/i,
    wordCruft: /<!--|mso-|<\s*\/?[a-z]+:[a-z]|<\?xml|<style|<xml|class=("|')?Mso/i,
  };

  const stripped = new Set<string>();
  for (const [k, rx] of Object.entries(flagged)) {
    if (rx.test(dirtyHtml)) stripped.add(k);
  }

  // ── Aggressive pre-clean for Word / Google Docs / LibreOffice paste:
  //    remove HTML comments, <style>/<script> blocks, <xml>/<o:p>/<w:*>
  //    namespaced tags, and any `<!--[if ... ]> ... <![endif]-->` conditionals.
  let pre = dirtyHtml
    // conditional comments first (Word wraps its cruft in these)
    .replace(/<!--\[if[\s\S]*?\[endif\]-->/gi, "")
    // any remaining HTML comments (Word stuffs entire stylesheets in one)
    .replace(/<!--[\s\S]*?-->/g, "")
    // <style>…</style> and <script>…</script> blocks in full
    .replace(/<style\b[\s\S]*?<\/style>/gi, "")
    .replace(/<script\b[\s\S]*?<\/script>/gi, "")
    // <xml>…</xml> blocks (Word metadata)
    .replace(/<xml\b[\s\S]*?<\/xml>/gi, "")
    // namespaced tags like <o:p>, <w:sectPr>, <v:shape> — drop the tags but
    // keep text content in case anything's in there.
    .replace(/<\/?[a-z]+:[a-z][a-z0-9]*\b[^>]*>/gi, "")
    // Word's tab marker: <span style="mso-tab-count:N">   </span> — replace
    // each with a real \t so CSS `tab-size` controls width on render.
    .replace(/<span\b[^>]*mso-tab-count[^>]*>[\s\S]*?<\/span>/gi, "\t")
    // Any leftover XML processing instructions
    .replace(/<\?[\s\S]*?\?>/g, "");

  const doc = new DOMParser().parseFromString(
    `<div id="root">${pre}</div>`,
    "text/html"
  );
  const root = doc.getElementById("root")!;

  // Nuke <style>, <script>, <link>, <meta>, <title> anywhere in the tree.
  root.querySelectorAll("style, script, link, meta, title").forEach((n) => n.remove());

  // Walk the tree, converting <b>/<strong> → plain text, stripping attrs.
  function walk(node: Node) {
    const children = Array.from(node.childNodes);
    for (const c of children) {
      if (c.nodeType === 8) { c.remove(); continue; } // comment
      if (c.nodeType === 3) continue;                 // text
      if (c.nodeType !== 1) { c.remove(); continue; }
      const el = c as Element;
      const tag = el.tagName;

      const styleAttr = el.getAttribute("style") || "";
      const italicFromStyle = /font-style\s*:\s*italic/i.test(styleAttr);

      if (!kept.has(tag)) {
        while (el.firstChild) el.parentNode!.insertBefore(el.firstChild, el);
        el.remove();
        continue;
      }

      while (el.attributes.length) el.removeAttribute(el.attributes[0].name);

      if (italicFromStyle && tag !== "EM" && tag !== "I") {
        const em = doc.createElement("em");
        while (el.firstChild) em.appendChild(el.firstChild);
        el.replaceWith(em);
        walk(em);
        continue;
      }
      walk(el);
    }
  }
  walk(root);

  // Unwrap remaining spans/divs (presentational leftovers).
  root.querySelectorAll("span,div").forEach((n) => {
    if (n.tagName === "SPAN" || (n.tagName === "DIV" && !n.hasAttribute("data-keep"))) {
      while (n.firstChild) n.parentNode!.insertBefore(n.firstChild, n);
      n.remove();
    }
  });

  // Strip ANY paragraph with no meaningful text content. This collapses:
  //   <p></p>, <p>&nbsp;</p>, <p><br></p>, <p><br><br></p>, <p>   </p>, etc.
  // Writers who pasted from Word often hit Enter twice between paragraphs,
  // leaving an empty paragraph in between — we don't want that in the
  // printed output.
  root.querySelectorAll("p").forEach((p) => {
    const t = (p.textContent || "").replace(/ /g, " ").trim();
    if (!t) p.remove();
  });


  if (!opts.preserveStructure) {
    // Strip leading <br>s at the very top of the fragment.
    while (root.firstChild && root.firstChild.nodeType === 1 && (root.firstChild as Element).tagName === "BR") {
      root.firstChild.remove();
    }
    // Strip <br>s that are siblings of <p> elements (TextEdit and some other
    // editors use these as blank-line dividers between paragraphs).
    Array.from(root.childNodes).forEach((n) => {
      if (n.nodeType === 1 && (n as Element).tagName === "BR") (n as Element).remove();
    });
  }
  // Collapse any remaining runs of 3+ consecutive <br>s to two.
  const walker = root.querySelectorAll("br");
  let run = 0;
  walker.forEach((br) => {
    const prev = br.previousSibling;
    if (prev && prev.nodeType === 1 && (prev as Element).tagName === "BR") {
      run++;
      if (run >= 2) br.remove();
    } else {
      run = 0;
    }
  });

  // Remove whitespace-only text nodes between block-level siblings. The
  // editor uses `white-space: pre-wrap` (so poets can indent lines), which
  // otherwise renders every inter-tag "\n" as a visible blank line.
  const BLOCKS = new Set(["P", "DIV", "BLOCKQUOTE", "PRE", "H1", "H2", "H3", "H4", "H5", "H6"]);
  const clearBetweenBlocks = (parent: Node) => {
    const kids = Array.from(parent.childNodes);
    for (const n of kids) {
      if (n.nodeType === 3 && !(n.textContent || "").trim()) {
        const prev = n.previousSibling;
        const next = n.nextSibling;
        const prevBlock = prev && prev.nodeType === 1 && BLOCKS.has((prev as Element).tagName);
        const nextBlock = next && next.nodeType === 1 && BLOCKS.has((next as Element).tagName);
        if (prevBlock || nextBlock || !prev || !next) n.remove();
      } else if (n.nodeType === 1) {
        clearBetweenBlocks(n);
      }
    }
  };
  clearBetweenBlocks(root);

  // Word's HTML export sprinkles literal \n and \t characters between <br>
  // tags as source-code indentation. Under `white-space: pre-wrap`, those
  // characters render as additional line breaks/tabs — doubling line spacing
  // and producing phantom indents. Strip \n from all text nodes; keep
  // intentional leading spaces (poets use them for indentation). Convert
  // stray \t to four spaces (Word's tab convention).
  const stripWhitespaceArtifacts = (node: Node) => {
    const walker = doc.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    const fix: Node[] = [];
    let cur: Node | null;
    while ((cur = walker.nextNode())) fix.push(cur);
    fix.forEach((tn: Node) => {
      let t = tn.textContent;
      if (!t) return;
      // Word wraps long lines in its HTML source with bare \n between words
      // (no surrounding space). Under `white-space: pre-wrap` those \ns would
      // render as visible line breaks AND, if we just deleted them, would
      // weld the adjacent words together ("and\nappear" → "andappear").
      // So: strip \n at the very start/end of a text node, and replace
      // internal \n with a single space. Also strip Unicode line/paragraph
      // separators (U+2028, U+2029) that TextEdit RTF puts in the clipboard —
      // Word renders these as visible placeholder boxes ("L SEP" / "P SEP").
      // Tabs are left intact so `tab-size` can control their width.
      let cleaned = t.replace(/\r/g, "");
      cleaned = cleaned.replace(/[\u2028\u2029]/g, "");
      cleaned = cleaned.replace(/^\n+/, "").replace(/\n+$/, "");
      cleaned = cleaned.replace(/\n+/g, " ");
      if (cleaned !== t) tn.textContent = cleaned;
    });
  };
  stripWhitespaceArtifacts(root);

  return { html: root.innerHTML, stripped: Array.from(stripped) };
}

// Poetry-specific paste normalizer. Many writers paste from Word/Pages/Docs
// where they hit Enter once for each line and twice for a stanza break —
// producing <p>line1</p><p>line2</p><p></p><p>line3</p>. In poetry mode we
// want <p>line1<br>line2</p><p>line3</p> so the printed output respects
// stanzas.
//
// Heuristics:
// 1. Runs on the raw clipboard HTML BEFORE sanitization (empty <p>'s are
//    still around at this point — sanitizeRichText strips them).
// 2. Only normalizes when no <p> already uses <br> for soft breaks. If the
//    paste already uses Word's "Shift+Enter" structure, we leave it alone.
// 3. Empty <p> = stanza divider. Consecutive non-empty <p>'s merge into one
//    <p> with <br>'s between them.
//
// Returns { html, normalized, originalParagraphs, mergedStanzas }.
function poetryNormalizeHtml(rawHtml: string): { html: string; normalized: boolean; lineCount?: number; stanzaCount?: number } {
  const doc = new DOMParser().parseFromString(`<div id="__pn">${rawHtml}</div>`, "text/html");
  const root = doc.getElementById("__pn")!;

  // Find direct-ish paragraph children (avoid recursing into nested tables etc).
  const paras = Array.from(root.querySelectorAll("p"));
  if (paras.length < 2) return { html: rawHtml, normalized: false };

  // If any <p> already uses <br> for soft breaks, the paste structure is
  // already poetry-friendly; don't molest it.
  const alreadyHasBr = paras.some((p) => p.querySelector("br"));
  if (alreadyHasBr) return { html: rawHtml, normalized: false };

  // Walk and group.
  const stanzas = [];
  let current = [];
  let hadEmptyDivider = false;

  for (const p of paras) {
    const text = p.textContent.replace(/ /g, " ").trim();
    if (!text) {
      if (current.length) {
        stanzas.push(current);
        current = [];
      }
      hadEmptyDivider = true;
    } else {
      // Keep inline HTML (so italics survive); strip wrapping <p>.
      current.push(p.innerHTML.trim());
    }
  }
  if (current.length) stanzas.push(current);

  if (!stanzas.length) return { html: rawHtml, normalized: false };

  // If we only ended up with 1 stanza AND no empty dividers existed AND every
  // input paragraph had a single line of text — that means the user pasted a
  // sequence of paragraphs with no blank lines. We still merge them into
  // one stanza of <br>-separated lines (since this is poetry mode and that's
  // what they almost always want).
  const merged = stanzas
    .map((lines) => `<p>${lines.join("<br>")}</p>`)
    .join("");

  // Did we actually change anything meaningful?
  const totalLines = stanzas.reduce((n, s) => n + s.length, 0);
  const normalized = hadEmptyDivider || totalLines > stanzas.length;

  return {
    html: normalized ? merged : rawHtml,
    normalized,
    lineCount: totalLines,
    stanzaCount: stanzas.length,
  };
}

// Shared cleaner for plain-text fallback pastes (Word sometimes dumps its
// whole CSS comment block into the text/plain channel). Handles the same
// cases as the HTML sanitizer but for a plain string.
function cleanPastedPlain(raw: string): { text: string; didStrip: boolean } {
  let t = String(raw || "");
  const before = t;
  // TextEdit RTF (and some other Cocoa apps) embed U+2028 (LINE SEPARATOR)
  // and U+2029 (PARAGRAPH SEPARATOR) in the clipboard. Word renders them
  // as visible placeholder boxes ("L SEP" / "P SEP"). Normalize them to
  // standard newlines so the rest of the pipeline treats them correctly.
  t = t.replace(/\u2029/g, "\n\n").replace(/\u2028/g, "\n");
  t = t.replace(/<!--[\s\S]*?-->/g, "");
  t = t.replace(/<!--[\s\S]*$/g, "");      // unterminated Word comment
  t = t.replace(/\[if[\s\S]*?\[endif\]/gi, "");
  t = t.replace(/<style\b[\s\S]*?<\/style>/gi, "");
  t = t.replace(/<script\b[\s\S]*?<\/script>/gi, "");
  t = t.replace(/<xml\b[\s\S]*?<\/xml>/gi, "");
  t = t.replace(/<\?[\s\S]*?\?>/g, "");
  t = t.replace(/<\/?[a-z][^>]*>/gi, "");
  t = t.replace(/@font-face\s*\{[\s\S]*?\}/gi, "");
  t = t.replace(/@page\b[^{]*\{[\s\S]*?\}/gi, "");
  // Any CSS-looking rule block (selector { … }) — catches p.MsoNormal et al.
  t = t.replace(/[.#]?[\w:\-,\s."*]+\{[^{}]*\}/g, "");
  // Bare "mso-*" lines and common CSS property lines left behind.
  t = t.replace(/^\s*(mso-|margin|font-|line-|page|panose|text-align|mso-[^:\n]*:).*$/gim, "");
  // Any remaining CSS-looking "prop: value;" line on its own
  t = t.replace(/^\s*[\w-]+\s*:\s*[^;\n]+;?\s*$/gm, "");
  t = t.replace(/\n{3,}/g, "\n\n").trim();
  return { text: t, didStrip: t !== before };
}

const STRIP_LABELS = {
  bold: "bold",
  underline: "underlines",
  color: "text color",
  background: "highlights",
  fontFamily: "custom fonts",
  fontSize: "font sizes",
  heading: "headings",
  list: "bulleted / numbered lists",
  table: "tables",
  image: "images",
  link: "hyperlinks",
  align: "alignment",
  strike: "strikethrough",
  wordCruft: "Word / Office metadata",
};

// ─────────────────────────────────────────────────────────────────────────────
// Visual line-break marker (↵) decoration.
//
// `::after` on <br> is not reliably rendered (some browsers ignore generated
// content on void elements entirely). So instead we keep an empty <span
// class="brk-mark"> immediately before each <br>; CSS draws the ↵ glyph via
// `::before` on the span, gated by `[data-show-marks="on"]` on the editor.
//
// Notes on safety:
//   • Markers are NOT contenteditable=false — that was experimentally found
//     to make the caret behave strangely around them. Letting the browser
//     treat them as ordinary inline content keeps editing normal; if the
//     user deletes one accidentally, the next render reinstates it.
//   • The form's stored value never contains markers — they're stripped
//     before being passed to the parent via onChange.
//   • Re-decoration is idempotent: orphaned markers (whose <br> was deleted)
//     are removed, and <br>s missing a marker get one. Nothing else moves.
function decorateBreaksInPlace(root: Element | null) {
  if (!root) return;
  // Drop orphaned markers (no <br> after them).
  Array.from(root.querySelectorAll("span.brk-mark")).forEach((span) => {
    const next = span.nextElementSibling;
    if (!next || next.tagName !== "BR") span.remove();
  });
  // Add a marker before any <br> that doesn't already have one.
  Array.from(root.querySelectorAll("br")).forEach((br) => {
    const prev = br.previousElementSibling;
    if (prev && prev.tagName === "SPAN" && (prev as Element).classList.contains("brk-mark")) return;
    const span = root.ownerDocument!.createElement("span");
    span.className = "brk-mark";
    br.parentNode!.insertBefore(span, br);
  });
}

function stripBreakDecorations(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(`<div id="__sb">${html}</div>`, "text/html");
  const root = doc.getElementById("__sb");
  if (!root) return html;
  Array.from(root.querySelectorAll("span.brk-mark")).forEach((s) => s.remove());
  return root.innerHTML;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rich-text editor (contentEditable) — italic ONLY. Sanitizes paste + typed
// input so colors, fonts, bold, highlights, headings, lists, etc. are stripped.
interface RichTextProps {
  id: string;
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  maxWords?: number;
  maxCharsPerLine?: number;
  genreMode?: Genre;
  placeholder?: string;
}
function RichText({
  id,
  value,
  onChange,
  minHeight = 220,
  maxWords,
  maxCharsPerLine,
  genreMode,
  placeholder,
}: RichTextProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<{ italic: boolean }>({ italic: false });
  const [focused, setFocused] = useState(false);
  const [lastStripped, setLastStripped] = useState<string[]>([]);
  const [poetryPasteNotice, setPoetryPasteNotice] = useState<{ lines: number; stanzas: number } | null>(null);
  // Show ¶ at end of each paragraph/stanza and ↵ at each soft line break.
  // Default ON whenever the writer has picked a genre — both poets and prose
  // writers benefit from seeing where they hit Return.
  const [showMarks, setShowMarks] = useState(genreMode === "poetry" || genreMode === "prose");

  useEffect(() => {
    // If genre changes, re-default the marks toggle to its sensible state.
    setShowMarks(genreMode === "poetry" || genreMode === "prose");
  }, [genreMode]);

  useEffect(() => {
    if (!ref.current) return;
    // Ensure Enter inserts <p> not <div>, so our paragraph spacing applies.
    try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch (e) { }
    // Compare against the *stripped* DOM so the marker decoration alone
    // doesn't trigger an innerHTML reset (which would collapse the caret).
    if (stripBreakDecorations(ref.current!.innerHTML) !== value) {
      ref.current!.innerHTML = value || "";
    }
    decorateBreaksInPlace(ref.current);
  }, [value]);

  const updateActive = useCallback(() => {
    try {
      setActive({ italic: document.queryCommandState("italic") });
    } catch (e) { }
  }, []);

  const exec = (cmd: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false);
    decorateBreaksInPlace(ref.current);
    onChange(stripBreakDecorations(ref.current!.innerHTML));
    updateActive();
  };

  // Insert a soft line break (<br>) at the caret. Equivalent to Shift+Return.
  const insertLineBreak = () => {
    ref.current?.focus();
    // insertLineBreak works in Chromium; fall back to insertHTML otherwise.
    if (!document.execCommand("insertLineBreak")) {
      document.execCommand("insertHTML", false, "<br>");
    }
    decorateBreaksInPlace(ref.current);
    onChange(stripBreakDecorations(ref.current!.innerHTML));
    updateActive();
  };

  // Insert a stanza break — i.e. split the current <p> at the caret and start
  // a new one. Equivalent to pressing Return.
  const insertStanzaBreak = () => {
    ref.current?.focus();
    // Modern browsers expose this as insertParagraph; older fallbacks split
    // the paragraph manually via insertHTML.
    let ok = false;
    try { ok = document.execCommand("insertParagraph"); } catch (e) { }
    if (!ok) {
      document.execCommand("insertHTML", false, "</p><p><br></p>");
    }
    decorateBreaksInPlace(ref.current);
    onChange(stripBreakDecorations(ref.current!.innerHTML));
    updateActive();
  };

  // Paste handler: sanitize clipboard HTML before inserting. Also cleans the
  // plain-text fallback because Word dumps its CSS comment block there.
  const onPaste = (e: React.ClipboardEvent) => {
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    e.preventDefault();
    let inserted = "";
    let strippedKinds: string[] = [];
    let poetryNotice: { lines: number; stanzas: number } | null = null;

    if (html) {
      // In poetry mode, normalize "1 paragraph = line, 2 = stanza break"
      // BEFORE we sanitize — sanitizeRichText would otherwise strip the
      // empty paragraphs we use as stanza dividers.
      let preHtml = html;
      if (genreMode === "poetry") {
        const pn = poetryNormalizeHtml(html);
        if (pn.normalized) {
          preHtml = pn.html;
          poetryNotice = { lines: pn.lineCount ?? 0, stanzas: pn.stanzaCount ?? 0 };
        }
      }
      const { html: clean, stripped } = sanitizeRichText(preHtml);
      strippedKinds = stripped;
      inserted = clean;
    } else if (text) {
      const { text: cleanedText, didStrip } = cleanPastedPlain(text);
      if (didStrip) strippedKinds = ["wordCruft"];
      const escaped = cleanedText
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      if (genreMode === "poetry") {
        // Poetry rule: blank line = stanza break (\n{2,}); single \n = <br>
        const stanzas = escaped.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
        inserted = stanzas
          .map((s) => `<p>${s.replace(/\n/g, "<br>")}</p>`)
          .join("");
        if (stanzas.length || escaped.includes("\n")) {
          const totalLines = stanzas.reduce((n, s) => n + s.split("\n").length, 0);
          if (totalLines > stanzas.length) {
            poetryNotice = { lines: totalLines, stanzas: stanzas.length };
          }
        }
      } else {
        // Prose: each newline (single or double) becomes its own paragraph.
        const paras = escaped.split(/\n+/).map((p) => p.trim()).filter(Boolean);
        inserted = paras.map((p) => `<p>${p}</p>`).join("");
      }
    }
    document.execCommand("insertHTML", false, inserted);
    decorateBreaksInPlace(ref.current);
    onChange(stripBreakDecorations(ref.current!.innerHTML));
    updateActive();
    setLastStripped(strippedKinds);
    setPoetryPasteNotice(poetryNotice);
  };

  // Block formatting commands the sanitizer would strip anyway. Keeps the
  // editor honest WYSIWYG instead of producing bold/underline output that
  // silently disappears on next paste or on submit.
  const onKeyDown = (e: React.KeyboardEvent) => {
    // Backspace at the very start of a <p> should merge with the previous
    // <p> using a soft line break — not raw-concatenate the text and not
    // leave a stray empty paragraph behind. The browser default does
    // neither reliably.
    if (e.key === "Backspace" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        // Find the enclosing <p> within the editor.
        let node: Node | null = range.startContainer;
        let curP: HTMLElement | null = null;
        while (node && node !== ref.current) {
          if (node.nodeType === 1 && (node as Element).tagName === "P") {
            curP = node as HTMLElement;
            break;
          }
          node = node.parentNode;
        }
        const prevSib = curP?.previousElementSibling as HTMLElement | null;
        if (curP && prevSib && prevSib.tagName === "P") {
          // Confirm caret is at the very start of this <p>.
          const probe = document.createRange();
          probe.selectNodeContents(curP);
          probe.setEnd(range.startContainer, range.startOffset);
          if (probe.toString().length === 0) {
            e.preventDefault();
            // If prevP is empty (just <br> placeholder), drop it and keep caret.
            const prevOnlyBr =
              prevSib.childNodes.length === 1 &&
              (prevSib.firstChild as Element)?.tagName === "BR";
            if (prevOnlyBr || prevSib.textContent === "") {
              prevSib.remove();
            } else {
              // Append a <br> (unless prevP already ends with one) and then
              // move all children of curP into prevP.
              const last = prevSib.lastChild;
              const endsWithBr =
                last && last.nodeType === 1 && (last as Element).tagName === "BR";
              if (!endsWithBr) {
                prevSib.appendChild(document.createElement("br"));
              }
              // Remember where curP's first child will land — that's where
              // we'll place the caret.
              const mergeIndex = prevSib.childNodes.length;
              while (curP.firstChild) {
                prevSib.appendChild(curP.firstChild);
              }
              curP.remove();
              // Caret: just before what used to be the first child of curP.
              const newRange = document.createRange();
              const target = prevSib.childNodes[mergeIndex];
              if (target) newRange.setStartBefore(target);
              else newRange.setStart(prevSib, mergeIndex);
              newRange.collapse(true);
              sel.removeAllRanges();
              sel.addRange(newRange);
            }
            onChange(ref.current!.innerHTML);
            updateActive();
            return;
          }
        }
      }
    }

    if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
    const k = e.key.toLowerCase();
    if (k === "b" || k === "u") e.preventDefault();
  };

  const onBeforeInput = (e: React.FormEvent<HTMLDivElement>) => {
    const inputType = (e.nativeEvent as InputEvent).inputType;
    const blocked = new Set([
      "formatBold",
      "formatUnderline",
      "formatStrikeThrough",
      "insertOrderedList",
      "insertUnorderedList",
    ]);
    if (blocked.has(inputType)) e.preventDefault();
  };

  const onInput = () => {
    // Strip whitespace-only text nodes immediately adjacent to <br> —
    // the browser sometimes inserts both, which doubles the visible break
    // under white-space: pre-wrap.
    try {
      const el = ref.current!;
      const brs = el.querySelectorAll("br");
      brs.forEach((br: Element) => {
        const next = br.nextSibling;
        if (next && next.nodeType === 3 && /^[\t\n\r ]+$/.test(next.textContent || "")) {
          next.textContent = "";
        }
        const prev = br.previousSibling;
        if (prev && prev.nodeType === 3 && /^[\t\n\r ]+$/.test(prev.textContent || "")) {
          prev.textContent = "";
        }
      });
      // Keep <br> markers in sync (idempotent: only inserts before bare
      // <br>s, removes orphaned markers).
      decorateBreaksInPlace(el);
    } catch (e) { }
    // Strip markers from the upstream value — they're a display concern only.
    onChange(stripBreakDecorations(ref.current!.innerHTML));
    updateActive();
  };

  // Convert HTML to plain text with proper line breaks. We walk the DOM
  // ourselves rather than relying on innerText, which on detached elements
  // doesn't honor <br>/<p> boundaries — that was causing the whole poem to
  // be reported as a single 1300-character "line".
  const plain = useMemo(() => {
    const html = value || "";
    if (!html.trim()) return "";
    const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, "text/html");
    const root = doc.getElementById("r")!;
    const parts: string[] = [];
    const BLOCK = new Set(["P", "DIV", "BLOCKQUOTE", "PRE", "H1", "H2", "H3", "H4", "H5", "H6", "LI"]);
    const walk = (node: Node) => {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === 3) {
          parts.push(child.textContent || "");
        } else if (child.nodeType === 1) {
          const tag = (child as Element).tagName;
          if (tag === "BR") {
            parts.push("\n");
          } else if (BLOCK.has(tag)) {
            walk(child);
            parts.push("\n");
          } else {
            walk(child);
          }
        }
      }
    };
    walk(root);
    return parts.join("").replace(/\n+$/, "");
  }, [value]);

  const words = useMemo(() => {
    const t = plain.trim();
    if (!t) return 0;
    return t.split(/\s+/).length;
  }, [plain]);

  const lines = useMemo(() => plain.split(/\n/), [plain]);
  const longLines = useMemo(() => {
    if (!maxCharsPerLine) return [];
    return lines
      .map((l, i) => ({ i, len: l.length }))
      .filter((l) => l.len > maxCharsPerLine);
  }, [lines, maxCharsPerLine]);

  const overWords = maxWords && words > maxWords;

  // Words that exceed the limit, joined into a single string for the
  // "over-limit" warning panel. e.g. with maxWords=500 and 510 words written,
  // this is words 501..510 joined by spaces.
  const overLimitText = useMemo(() => {
    if (!maxWords) return "";
    const t = plain.trim();
    if (!t) return "";
    const all = t.split(/\s+/);
    if (all.length <= maxWords) return "";
    return all.slice(maxWords).join(" ");
  }, [plain, maxWords]);

  return (
    <div className="rte" data-focused={focused || undefined}>
      <div className="rte-toolbar" role="toolbar" aria-label="Formatting">
        <button
          type="button"
          className="rte-btn"
          data-active={active.italic || undefined}
          onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}
          aria-label="Italic"
          title="Italic (only italics are preserved)"
        >
          <em>I</em>
        </button>
        {(genreMode === "poetry" || genreMode === "prose") && (
          <>
            <div className="rte-toolbar-divider" aria-hidden="true" />
            <button
              type="button"
              className="rte-btn rte-btn-text"
              onMouseDown={(e) => { e.preventDefault(); insertLineBreak(); }}
              aria-label="Insert line break"
              title={genreMode === "poetry"
                ? "Line break (Shift+Return) — next line, same stanza"
                : "Line break (Shift+Return) — within the same paragraph"}
            >
              <span className="rte-btn-mark" aria-hidden="true">↵</span>
              <span className="rte-btn-text-label">Line</span>
            </button>
            <button
              type="button"
              className="rte-btn rte-btn-text"
              onMouseDown={(e) => { e.preventDefault(); insertStanzaBreak(); }}
              aria-label={genreMode === "poetry" ? "Insert stanza break" : "Insert paragraph break"}
              title={genreMode === "poetry"
                ? "Stanza break (Return) — start a new stanza"
                : "Paragraph break (Return) — start a new paragraph"}
            >
              <span className="rte-btn-mark" aria-hidden="true">¶</span>
              <span className="rte-btn-text-label">{genreMode === "poetry" ? "Stanza" : "Paragraph"}</span>
            </button>
            <button
              type="button"
              className="rte-btn rte-btn-text"
              data-active={showMarks || undefined}
              onMouseDown={(e) => { e.preventDefault(); setShowMarks((v) => !v); ref.current?.focus(); }}
              aria-pressed={showMarks}
              title={showMarks ? "Hide formatting marks" : "Show formatting marks (¶ paragraph, ↵ line)"}
            >
              <span className="rte-btn-text-label">{showMarks ? "Hide marks" : "Show marks"}</span>
            </button>
          </>
        )}
        <div className="rte-toolbar-hint">
          {genreMode === "poetry"
            ? "Italics + line/stanza breaks"
            : genreMode === "prose"
              ? "Italics + line/paragraph breaks"
              : "Italics only"}
        </div>
        <div className="rte-toolbar-spacer" />
        <div className="rte-counter">
          <span data-warn={overWords || undefined}>
            {words.toLocaleString()} {words === 1 ? "word" : "words"}
          </span>
          {maxWords && (
            <span className="rte-counter-sep"> / {maxWords}</span>
          )}
        </div>
      </div>
      {lastStripped.length > 0 && (
        <div className="rte-stripped">
          <div className="rte-stripped-head">
            <span className="rte-stripped-mark" aria-hidden="true">!</span>
            <span>
              We cleaned your paste. The anthology only preserves <em>italics</em> — we removed:
              {" "}
              <strong>{lastStripped.map((k) => (STRIP_LABELS as Record<string, string>)[k] || k).join(", ")}</strong>.
            </span>
            <button
              type="button"
              className="rte-stripped-dismiss"
              onClick={() => setLastStripped([])}
              aria-label="Dismiss"
            >×</button>
          </div>
        </div>
      )}
      {poetryPasteNotice && (
        <div className="rte-stripped">
          <div className="rte-stripped-head">
            <span className="rte-stripped-mark" aria-hidden="true">¶</span>
            <span>
              We read your paste as poetry — <strong>{poetryPasteNotice.lines} line{poetryPasteNotice.lines === 1 ? "" : "s"}</strong>{" "}
              across <strong>{poetryPasteNotice.stanzas} stanza{poetryPasteNotice.stanzas === 1 ? "" : "s"}</strong>.
              Single paragraph breaks became line breaks; blank lines became stanza breaks.
            </span>
            <button
              type="button"
              className="rte-stripped-dismiss"
              onClick={() => setPoetryPasteNotice(null)}
              aria-label="Dismiss"
            >×</button>
          </div>
        </div>
      )}
      {genreMode === "poetry" && longLines.length > 0 && (
        <div className="rte-warn">
          {longLines.length === 1
            ? `Line ${longLines[0].i + 1} is ${longLines[0].len} characters — keep lines to ≤ ${maxCharsPerLine}.`
            : `${longLines.length} lines exceed ${maxCharsPerLine} characters.`}
        </div>
      )}
      <div
        id={id}
        ref={ref}
        className="rte-area"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-empty={!plain || undefined}
        data-placeholder={placeholder || ""}
        data-show-marks={showMarks ? "on" : undefined}
        data-genre={genreMode || undefined}
        style={{ minHeight }}
        onInput={onInput}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onBeforeInput={onBeforeInput}
        onKeyUp={updateActive}
        onMouseUp={updateActive}
        onFocus={() => { setFocused(true); updateActive(); }}
        onBlur={() => setFocused(false)}
      />
      {overLimitText && (
        <div className="rte-over-limit" role="status">
          <div className="rte-over-limit-head">
            Over by <strong>{words - maxWords!}</strong> word{(words - maxWords!) === 1 ? "" : "s"} — text after the limit:
          </div>
          <div className="rte-over-limit-text">{overLimitText}</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Normalize for name-in-sentence comparison: lowercase, strip diacritics + punctuation.
function normalizeForMatch(s: string): string {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Pull the first sentence from free-form prose. Falls back to the whole string.
function firstSentence(text: string): string {
  const t = (text || "").trim();
  if (!t) return "";
  // Match up to the first sentence terminator followed by whitespace or end.
  const m = t.match(/^[^.!?\n]*[.!?](?=\s|$)/);
  return (m ? m[0] : t).trim();
}

// Scan free-form text for first-person pronouns. Returns the unique set of
// matches as they appear in the text (preserving case), so the warning can
// quote the actual word the writer used ("I", "my", etc.). Bios should be
// in third person — this powers a soft warning, not a hard validation.
function findFirstPersonPronouns(text: string): string[] {
  const t = String(text || "");
  if (!t.trim()) return [];
  const patterns = [
    /\bI\b/g,                       // capitalized "I"
    /\b[Ii]'(?:m|ve|ll|d|M|VE|LL|D)\b/g,  // I'm, I've, I'll, I'd
    /\b(?:me|my|mine|myself)\b/gi,  // me, my, mine, myself
  ];
  const found = new Set<string>();
  for (const p of patterns) {
    const matches = t.match(p);
    if (matches) matches.forEach((m) => found.add(m));
  }
  return Array.from(found);
}

// Check whether the first sentence of `bio` contains the contributor's name.
// Returns { ok, hasAny, hasAll, tokens, firstSentence }.
function checkNameInFirstSentence(bio: string, displayName: string) {
  const first = firstSentence(bio);
  const nameTokens = normalizeForMatch(displayName)
    .split(" ")
    .filter((t: string) => t.length >= 2); // ignore initials / particles
  if (!first || nameTokens.length === 0) {
    return { ok: false, hasAny: false, hasAll: false, tokens: nameTokens, firstSentence: first };
  }
  const firstNorm = " " + normalizeForMatch(first) + " ";
  const found = nameTokens.filter((tok: string) => firstNorm.includes(" " + tok + " ") || firstNorm.includes(" " + tok));
  const hasAll = found.length === nameTokens.length;
  const hasAny = found.length > 0;
  return { ok: hasAll, hasAny, hasAll, tokens: nameTokens, foundTokens: found, firstSentence: first };
}

interface PlainTextareaProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  minHeight?: number;
  placeholder?: string;
  maxWords?: number;
  displayName?: string;
}
function PlainTextarea({ id, value, onChange, minHeight = 180, placeholder, maxWords, displayName }: PlainTextareaProps) {
  // Despite the legacy name "PlainTextarea", this is now a contentEditable
  // richer editor that preserves italics — same italics-only behavior as the
  // body editor. The component still accepts a string `value` and emits a
  // string `onChange`, but the string is sanitized HTML (only <em>/<i>/<br>/<p>).
  const bioRef = useRef<HTMLDivElement | null>(null);
  const [stripped, setStripped] = useState(false);
  const [italicActive, setItalicActive] = useState(false);

  // Plain-text extraction — for word count, name check, first-person check.
  const plain = useMemo(() => {
    const html = value || "";
    if (!html.trim()) return "";
    try {
      const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, "text/html");
      const root = doc.getElementById("r");
      if (!root) return "";
      const parts: string[] = [];
      const BLOCK = new Set(["P", "DIV", "BLOCKQUOTE", "PRE", "H1", "H2", "H3", "H4", "H5", "H6", "LI"]);
      const walk = (node: Node) => {
        for (const child of Array.from(node.childNodes)) {
          if (child.nodeType === 3) parts.push(child.textContent || "");
          else if (child.nodeType === 1) {
            const el = child as HTMLElement;
            if (el.tagName === "BR") parts.push("\n");
            else if (BLOCK.has(el.tagName)) { walk(child); parts.push("\n"); }
            else walk(child);
          }
        }
      };
      walk(root);
      return parts.join("").replace(/\n+$/, "");
    } catch {
      return String(value || "").replace(/<[^>]+>/g, "");
    }
  }, [value]);

  // Sync external value → DOM (only when different, to avoid cursor jumps).
  useEffect(() => {
    if (!bioRef.current) return;
    if (bioRef.current.innerHTML !== (value || "")) {
      bioRef.current.innerHTML = value || "";
    }
  }, [value]);

  const words = useMemo(() => {
    const t = plain.trim();
    if (!t) return 0;
    return t.split(/\s+/).length;
  }, [plain]);
  const under = maxWords && words < 7;
  const over = maxWords && words > maxWords;

  // Words past the limit, joined into a single string for the warning panel.
  const overLimitText = useMemo(() => {
    if (!maxWords) return "";
    const t = plain.trim();
    if (!t) return "";
    const all = t.split(/\s+/);
    if (all.length <= maxWords) return "";
    return all.slice(maxWords).join(" ");
  }, [plain, maxWords]);

  const nameCheck = useMemo(
    () => checkNameInFirstSentence(plain, displayName ?? ""),
    [plain, displayName]
  );
  const bioHasContent = plain.trim().length > 0;
  const nameProvided = (displayName || "").trim().length > 0;
  const showNameWarn = bioHasContent && nameProvided && !nameCheck.ok;

  const firstPersonHits = useMemo(
    () => findFirstPersonPronouns(plain),
    [plain]
  );
  const showFirstPersonWarn = bioHasContent && firstPersonHits.length > 0;

  const updateActive = useCallback(() => {
    try { setItalicActive(document.queryCommandState("italic")); } catch { }
  }, []);

  const exec = (cmd: string) => {
    bioRef.current?.focus();
    document.execCommand(cmd, false);
    if (bioRef.current) onChange(bioRef.current.innerHTML);
    updateActive();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    e.preventDefault();
    let inserted = "";
    let didStrip = false;

    if (html) {
      const { html: clean, stripped: stripKinds } = sanitizeRichText(html);
      inserted = clean;
      if (stripKinds.length) didStrip = true;
    } else if (text) {
      const { text: cleanedText, didStrip: ds } = cleanPastedPlain(text);
      didStrip = ds;
      const escaped = cleanedText
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      // Bio is prose — each newline becomes a paragraph break.
      const paras = escaped.split(/\n+/).map((p) => p.trim()).filter(Boolean);
      inserted = paras.map((p) => `<p>${p}</p>`).join("");
    }
    document.execCommand("insertHTML", false, inserted);
    if (bioRef.current) onChange(bioRef.current.innerHTML);
    setStripped(didStrip);
    updateActive();
  };

  const onInput = () => {
    if (bioRef.current) onChange(bioRef.current.innerHTML);
    updateActive();
  };

  return (
    <div className="rte">
      <div className="rte-toolbar">
        <button
          type="button"
          className="rte-btn"
          data-active={italicActive || undefined}
          onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}
          aria-label="Italic"
          title="Italic (only italics are preserved)"
        >
          <em>I</em>
        </button>
        <div className="rte-toolbar-hint">Italics only</div>
        <div className="rte-toolbar-spacer" />
        <div className="rte-counter">
          <span data-warn={(over || under) || undefined}>
            {words.toLocaleString()} {words === 1 ? "word" : "words"}
          </span>
          {maxWords && <span className="rte-counter-sep"> / 7–{maxWords}</span>}
        </div>
      </div>
      {stripped && (
        <div className="rte-stripped">
          <div className="rte-stripped-head">
            <span className="rte-stripped-mark" aria-hidden="true">!</span>
            <span>
              We cleaned your paste — removed <strong>Word / Office metadata</strong>.
            </span>
            <button
              type="button"
              className="rte-stripped-dismiss"
              onClick={() => setStripped(false)}
              aria-label="Dismiss"
            >×</button>
          </div>
        </div>
      )}
      <div
        id={id}
        ref={bioRef}
        className="rte-area"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-empty={!plain || undefined}
        data-placeholder={placeholder || ""}
        style={{ minHeight }}
        onInput={onInput}
        onPaste={onPaste}
        onKeyUp={updateActive}
        onMouseUp={updateActive}
        onFocus={updateActive}
      />
      {showNameWarn && (
        <div className="bio-rule-warn" role="status">
          <span className="bio-rule-warn-mark" aria-hidden="true">!</span>
          <div className="bio-rule-warn-body">
            <div className="bio-rule-warn-title">
              Pen name <em>{displayName}</em> must be in the first sentence of the biography.
              {nameCheck.hasAny && (
                <> Missing: <em>{nameCheck.tokens.filter((t: string) => !(nameCheck.foundTokens ?? []).includes(t)).join(" ")}</em>.</>
              )}
            </div>
            {nameCheck.firstSentence && (
              <div className="bio-rule-warn-quote">
                First sentence detected: <q>{nameCheck.firstSentence}</q>
              </div>
            )}
          </div>
        </div>
      )}
      {showFirstPersonWarn && (
        <div className="bio-rule-warn" role="status">
          <span className="bio-rule-warn-mark" aria-hidden="true">!</span>
          <div className="bio-rule-warn-body">
            <div className="bio-rule-warn-title">
              Your biography should be in the <em>third person</em>. We found first-person language: {firstPersonHits.map((w, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? ", " : ""}<strong>“{w}”</strong>
                </React.Fragment>
              ))}.
            </div>
            <div className="bio-rule-warn-quote">
              Try recasting as “{displayName || "[Your name]"} writes…” instead of “I write…”
            </div>
          </div>
        </div>
      )}
      {overLimitText && (
        <div className="rte-over-limit" role="status">
          <div className="rte-over-limit-head">
            Over by <strong>{words - maxWords!}</strong> word{(words - maxWords!) === 1 ? "" : "s"} — text after the limit:
          </div>
          <div className="rte-over-limit-text">{overLimitText}</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Checkbox({ id, label, checked, onChange }: { id: string; label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="check" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="check-box" aria-hidden="true">
        <svg viewBox="0 0 12 10" width="12" height="10"><path d="M1 5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
      <span className="check-label">{label}</span>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Editorial "Note" card — replaces the yellow warning boxes with typography.
function EditorsNote({ tone = "caution", title = "Editor's note", children }: { tone?: string; title?: ReactNode; children?: ReactNode }) {
  return (
    <aside className="note" data-tone={tone}>
      <div className="note-rail" aria-hidden="true" />
      <div className="note-body">
        <div className="note-title">
          <span className="note-kicker">{title}</span>
        </div>
        <div className="note-content">{children}</div>
      </div>
    </aside>
  );
}

export {
  Field,
  TextInput,
  EmailInput,
  GenreSelect,
  RichText,
  PlainTextarea,
  Checkbox,
  EditorsNote,
  checkNameInFirstSentence,
  sanitizeRichText,
};
